import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { messageFromError } from "../../../shared/api/drfErrors";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";

import PropertyFilterPanel from "./PropertyFilterPanel";
import PropertySearchToolbar from "./PropertySearchToolbar";

import { fetchProperties } from "../api/realty";
import {
  accumulatePropertyRows,
  deriveHasNext,
  isMapFetchAbortError,
  MARKER_PAGE_SIZE,
  parseMapCoordinate,
  propertyHasValidCoordinates,
  propertyRowId,
  unwrapPaginatedList,
} from "../lib/mapProgressiveResults";
import {
  DEFAULT_SORT,
  EMPTY_FILTER,
  buildBackendParams,
  buildPairedSurfaceHref,
  mergeUrlHydratedFilters,
  normalizeSearchSort,
  orderingFromSearchSort,
  propertyCompensationFilterErrorMessage,
  readFiltersFromSearch,
  shouldDeferPropertyFetchUntilSettlementVerified,
  shouldSkipPropertyFetchForSettlement,
  settlementVerificationErrorMessage,
  validatePropertyCompensationFilters,
  writeFiltersToUrl,
} from "../lib/searchFilterContract";

// RealtyMapScreenContent — /map canonical ledger.
//
// Substrate: design/map.html #Csection (.search-section--map).
// Topology (preserved verbatim — modernize.css 7064-7374):
//
//   .search-section.search-section--map > <this widget>
//     > .search-results.map-results
//         > <PropertyFilterPanel /> (.search-filter aside + offcanvas)
//         > <PropertySearchToolbar /> (.search-toolbar — countNode + map active, sort hidden)
//         > .map-canvas-wrap
//             > div#propertyMap.map-canvas    (Leaflet mount)
//             > .map-floating
//                 > button#mapFitAll.map-floating__btn
//                 > button#mapGeolocate.map-floating__btn
//             > .feature-realty-list__map-progress (load-more / append error)
//
// Paired-surface invariant (design-map.md §5 map):
//   * /map and /search are the same query rendered two ways.
//   * Filter shape is identical → PropertyFilterPanel is reused
//     verbatim, with the same URL bag + buildBackendParams contract.
//   * View toggle treats them as one surface in two modes.
//
// Leaflet integration:
//   * window.L global (CDN, public/index.html lines 38-42 — already
//     loaded for R3.6 DetailLocation).
//   * Map instance + marker layer live in screen-local refs.
//   * Mount-once effect on widget mount.
//   * Marker registry is keyed by Property id: page-1 / new-query
//     clears and may auto-fit; page-2+ appends markers without
//     fitBounds or remounting the map.
//   * Cleanup via map.remove() in try/catch on unmount.
//   * NO map manager, NO clustering, NO shared geo state, NO
//     react-leaflet adapter, NO viewport-driven backend fetches.
//
// Backend reconciliation (per backend/app/apps/realty/filters.py
// PropertyFilter + serializers.py LocationSerializer):
//   * Filter params: identical to /search (paired-surface).
//   * Geo transport: PropertyListSerializer nests LocationSerializer
//     which exposes latitude / longitude (DecimalField → string).
//   * Progressive accumulation: page_size=100 with explicit page=N.
//     DRF `next` is the authoritative hasNext signal. One user action
//     loads one additional page. Loaded rows are not the same as
//     visible markers (coordinate-less rows still count).
//
// Engagement state (likes/bookmarks) and per-card action menus live on
// /search and /detail. The map shows price-pin markers + popup with
// title/price/specs and a "Деталі" link to /realty/:id. No card-level
// mutation from the popup — preserves design-map.md §3 grammar.

// ===========================================================
// Filter/sort URL <-> bag — RIC-5.6C shared contract in
// ../lib/searchFilterContract.js. Map-specific ordering +
// page_size remain screen-local below.
// ===========================================================

function buildMapRequestParams(filters, page = 1) {
  return {
    ...buildBackendParams(filters),
    ordering: orderingFromSearchSort(DEFAULT_SORT),
    page_size: MARKER_PAGE_SIZE,
    page,
  };
}

function abortController(controllerRef) {
  const pending = controllerRef.current;
  if (!pending) return;
  try {
    pending.abort();
  } catch (_err) {
    /* ignore */
  }
  if (controllerRef.current === pending) {
    controllerRef.current = null;
  }
}

// ===========================================================
// Marker + popup HTML builders (kept local — divIcon uses raw HTML
// strings, so we escape backend-sourced text aggressively).
// ===========================================================

const TYPE_LABEL = {
  apartment: "Квартира",
  house: "Будинок",
  land: "Земельна ділянка",
  commercial: "Комерційна",
  industrial: "Промислова",
};

function escapeHtml(value) {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatPrice(price, currency) {
  if (price === null || price === undefined || price === "") return "";
  const n = Number(price);
  const formatted = Number.isFinite(n) ? n.toLocaleString("uk-UA") : price;
  const symbol = currency === "USD" ? "$" : currency === "EUR" ? "€" : "";
  return symbol ? `${symbol} ${formatted}` : `${formatted} ${escapeHtml(currency || "")}`;
}

function buildMarkerIcon(L, property) {
  const dealType = property.deal_type;
  const priceText = formatPrice(property.price, property.currency);
  const rentSuffix = dealType === "rent" ? '<small> /міс</small>' : "";

  const html =
    '<div class="map-pin">' +
    `  <span class="map-pin__price">${escapeHtml(priceText)}${rentSuffix}</span>` +
    '  <span class="map-pin__tail" aria-hidden="true"></span>' +
    "</div>";

  return L.divIcon({
    className: "map-pin-wrap" + (dealType === "rent" ? " map-pin-wrap--rent" : ""),
    html,
    iconSize: [110, 36],
    iconAnchor: [55, 36],
    popupAnchor: [0, -32],
  });
}

function buildPopupHtml(property) {
  const detailHref = `/realty/${escapeHtml(property.id)}`;
  const photo = property.main_image || "";
  const title = escapeHtml(property.title || "");
  const priceText = formatPrice(property.price, property.currency);
  const rentSuffix =
    property.deal_type === "rent" ? ' <small>/ міс</small>' : "";

  // Specs line: rooms (residential) · area m². Backend nests rooms
  // under .residential and area under .residential / .commercial /
  // .land — defensive optional chaining.
  const rooms =
    property.residential && property.residential.rooms !== undefined &&
    property.residential.rooms !== null
      ? `${escapeHtml(property.residential.rooms)} кімн.`
      : "";
  const area =
    (property.residential && property.residential.total_area_m2) ||
    (property.commercial && property.commercial.total_area_m2) ||
    (property.land && property.land.area_m2) ||
    null;
  const areaText = area ? `${escapeHtml(area)} м²` : "";
  const specs = [rooms, areaText].filter(Boolean).join(" · ");

  const loc = property.location || {};
  const settlement = escapeHtml(
    loc.settlement_name
    || (loc.settlement_unit && loc.settlement_unit.name)
    || "",
  );
  const localArea = escapeHtml(loc.local_area || "");
  const locText = settlement + (localArea ? `, ${localArea}` : "");

  const dealLabel = property.deal_type === "rent" ? "Оренда" : "Продаж";
  const dealClass =
    property.deal_type === "rent" ? "deal-pill--rent" : "deal-pill--sale";
  const typeLabel = TYPE_LABEL[property.property_type] || escapeHtml(property.property_type || "");

  return (
    '<article class="map-popup">' +
    (photo
      ? `<a href="${detailHref}" class="map-popup__photo"><img src="${escapeHtml(photo)}" alt=""></a>`
      : "") +
    '  <div class="map-popup__body">' +
    `    <h4 class="map-popup__title"><a href="${detailHref}">${title}</a></h4>` +
    `    <p class="map-popup__price"><strong>${escapeHtml(priceText)}</strong>${rentSuffix}</p>` +
    (specs ? `    <p class="map-popup__specs">${specs}</p>` : "") +
    (locText
      ? `    <p class="map-popup__loc"><i class="fa-solid fa-location-dot"></i>${locText}</p>`
      : "") +
    '    <div class="map-popup__tags">' +
    `      <span class="deal-pill ${dealClass}">${dealLabel}</span>` +
    `      <span class="type-pill-min">${typeLabel}</span>` +
    "    </div>" +
    `    <a href="${detailHref}" class="map-popup__link">Деталі<i class="fa-solid fa-arrow-right"></i></a>` +
    "  </div>" +
    "</article>"
  );
}

function extendBoundsFromProperty(L, bounds, property) {
  const loc = (property && property.location) || {};
  const lat = parseMapCoordinate(loc.latitude);
  const lng = parseMapCoordinate(loc.longitude);
  if (lat === null || lng === null) return;
  bounds.extend([lat, lng]);
}

function fitMapToProperties(map, L, properties) {
  if (!map || !L) return;
  const bounds = L.latLngBounds([]);
  for (const property of properties || []) {
    extendBoundsFromProperty(L, bounds, property);
  }
  if (bounds.isValid()) {
    try {
      map.fitBounds(bounds, { padding: FIT_PADDING, maxZoom: FIT_MAX_ZOOM });
    } catch (_err) {
      /* ignore — map may have been torn down */
    }
  }
}

function addPropertyMarker(L, layer, property, registry) {
  const id = propertyRowId(property);
  if (id === null || registry.has(id) || !propertyHasValidCoordinates(property)) {
    return;
  }
  const loc = property.location || {};
  const marker = L.marker(
    [parseMapCoordinate(loc.latitude), parseMapCoordinate(loc.longitude)],
    { icon: buildMarkerIcon(L, property) },
  ).bindPopup(buildPopupHtml(property), {
    className: "map-popup-wrap",
    maxWidth: 280,
    autoPanPadding: [40, 40],
  });
  marker.addTo(layer);
  registry.set(id, marker);
}

function clearMarkerRegistry(layer, registry) {
  if (layer && typeof layer.clearLayers === "function") {
    try {
      layer.clearLayers();
    } catch (_err) {
      /* ignore */
    }
  }
  registry.clear();
}

// ===========================================================
// Component
// ===========================================================

const UKRAINE_CENTER = [49.4, 31.0];
const UKRAINE_ZOOM = 6;
const FIT_PADDING = [60, 60];
const FIT_MAX_ZOOM = 12;
const OSM_TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

function RealtyMapScreenContent() {
  const location = useLocation();
  const navigate = useNavigate();

  // ---------- URL-hydrated applied state ----------

  const initial = useMemo(
    () => readFiltersFromSearch(location.search),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [appliedFilters, setAppliedFilters] = useState(initial);
  const [filterDraft, setFilterDraft] = useState(initial);
  const filterDraftRef = useRef(initial);
  filterDraftRef.current = filterDraft;

  // Paired-surface sort token for /search ↔ /map href + URL writes.
  // Map marker fetch always uses map-owned DEFAULT_SORT ordering; this
  // value is never applied to buildMapRequestParams.
  const pairedSort = useMemo(() => {
    const sortParam = new URLSearchParams(location.search).get("sort");
    return normalizeSearchSort(sortParam);
  }, [location.search]);

  const syncFilterDraft = (updater) => {
    setFilterDraft((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      filterDraftRef.current = next;
      return next;
    });
  };

  // ---------- Result / pagination state ----------

  const [results, setResults] = useState([]);
  const [count, setCount] = useState(null);
  const [loadedPage, setLoadedPage] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [appendLoading, setAppendLoading] = useState(false);
  const [appendError, setAppendError] = useState(null);

  // ---------- Geolocate state (button-local) ----------

  const [geoLoading, setGeoLoading] = useState(false);

  // ---------- Leaflet CDN-missing containment (R8.4.3) ----------
  //
  // window.L is set by the parser-blocking <script> in public/index.html
  // before the React bundle runs, so a one-shot mount-time probe is
  // enough: either the dependency exists or it failed permanently
  // (network, SRI integrity, adblock). No polling, no detector. When
  // missing, the map canvas is replaced by an explicit EmptyState so
  // the user can distinguish a runtime asset failure from an empty
  // result set. Floating controls stay mounted — their existing
  // !L || !map guards no-op safely.
  const [mapMissing, setMapMissing] = useState(false);

  // ---------- Map + request ownership refs ----------

  const mapElRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersLayerRef = useRef(null);
  const markersByIdRef = useRef(new Map());
  const shouldAutoFitRef = useRef(true);
  const queryGenRef = useRef(0);
  const abortRef = useRef(null);
  const appendInFlightRef = useRef(false);
  const queryParamsRef = useRef(null);
  const resultsRef = useRef(results);
  resultsRef.current = results;
  const loadedPageRef = useRef(loadedPage);
  loadedPageRef.current = loadedPage;
  const hasNextRef = useRef(hasNext);
  hasNextRef.current = hasNext;

  const resetAccumulatorForNewQuery = () => {
    shouldAutoFitRef.current = true;
    appendInFlightRef.current = false;
    queryParamsRef.current = null;
    setResults([]);
    setCount(null);
    setLoadedPage(0);
    setHasNext(false);
    setAppendLoading(false);
    setAppendError(null);
    const layer = markersLayerRef.current;
    clearMarkerRegistry(layer, markersByIdRef.current);
  };

  // ---------- Re-sync on URL change ----------

  useEffect(() => {
    syncFilterDraft((prev) => mergeUrlHydratedFilters(
      readFiltersFromSearch(location.search),
      prev,
    ));
  }, [location.search]);

  useEffect(() => {
    const urlFilters = readFiltersFromSearch(location.search);
    const resolved = mergeUrlHydratedFilters(
      urlFilters,
      filterDraftRef.current,
    );

    setAppliedFilters(resolved);

    const gen = ++queryGenRef.current;
    abortController(abortRef);
    resetAccumulatorForNewQuery();

    if (shouldSkipPropertyFetchForSettlement(resolved)) {
      setLoadState("error");
      setLoadError(settlementVerificationErrorMessage(resolved));
      return undefined;
    }

    if (shouldDeferPropertyFetchUntilSettlementVerified(resolved)) {
      setLoadState("loading");
      setLoadError(null);
      return undefined;
    }

    // Validate compensation filters before fetch — invalid tokens surface as
    // error state without broadening the query to the backend.
    const compensationValidation = validatePropertyCompensationFilters(resolved);
    if (!compensationValidation.valid) {
      setLoadState("error");
      setLoadError(
        propertyCompensationFilterErrorMessage(compensationValidation.errors),
      );
      return undefined;
    }

    setLoadState("loading");
    setLoadError(null);

    const params = buildMapRequestParams(resolved, 1);
    queryParamsRef.current = params;
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    fetchProperties(params, { signal: ctrl.signal })
      .then((data) => {
        if (gen !== queryGenRef.current) return;
        const unwrapped = unwrapPaginatedList(data);
        const rows = accumulatePropertyRows([], unwrapped.results);
        setResults(rows);
        setCount(unwrapped.count);
        setLoadedPage(1);
        setHasNext(deriveHasNext(unwrapped, rows.length));
        setLoadState("loaded");
      })
      .catch((err) => {
        if (gen !== queryGenRef.current) return;
        if (ctrl.signal.aborted || isMapFetchAbortError(err)) return;
        setLoadError(messageFromError(err));
        setLoadState("error");
      })
      .finally(() => {
        if (abortRef.current === ctrl) {
          abortRef.current = null;
        }
      });

    return () => {
      if (queryGenRef.current === gen) {
        queryGenRef.current += 1;
      }
      abortController(abortRef);
    };
    // resetAccumulatorForNewQuery is a stable local helper for this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.search,
    filterDraft.settlement_code_verified,
    filterDraft.settlement_verification_status,
  ]);

  // ---------- Leaflet mount (once on widget mount) ----------

  useEffect(() => {
    if (typeof window === "undefined" || !window.L) {
      setMapMissing(true);
      return undefined;
    }
    if (!mapElRef.current) return undefined;

    const L = window.L;
    const map = L.map(mapElRef.current, {
      zoomControl: true,
      scrollWheelZoom: true,
    }).setView(UKRAINE_CENTER, UKRAINE_ZOOM);

    L.tileLayer(OSM_TILE_URL, {
      maxZoom: 19,
      attribution: OSM_ATTRIBUTION,
    }).addTo(map);

    const markersLayer = L.layerGroup().addTo(map);

    mapInstanceRef.current = map;
    markersLayerRef.current = markersLayer;
    const markersById = markersByIdRef.current;

    // Force a re-measure once after fonts/CSS settle. Mirrors
    // design/js/map.js:151.
    const settleTimeout = window.setTimeout(() => {
      try {
        mapInstanceRef.current?.invalidateSize();
      } catch (_err) {
        /* ignore — DOM gone */
      }
    }, 100);

    const onResize = () => {
      try {
        mapInstanceRef.current?.invalidateSize();
      } catch (_err) {
        /* ignore — DOM gone */
      }
    };
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      window.clearTimeout(settleTimeout);
      try {
        mapInstanceRef.current?.remove();
      } catch (_err) {
        /* ignore — DOM already detached */
      }
      mapInstanceRef.current = null;
      markersLayerRef.current = null;
      markersById.clear();
    };
  }, []);

  // ---------- Incremental marker projection ----------
  //
  // Page-1 / new-query: registry is already cleared; add current
  // valid-coordinate rows and auto-fit once if appropriate.
  // Page-N append: add markers only for newly accumulated ids; do
  // not clearLayers; do not fitBounds.
  useEffect(() => {
    const L = typeof window !== "undefined" ? window.L : null;
    const map = mapInstanceRef.current;
    const layer = markersLayerRef.current;
    if (!L || !map || !layer) return;

    const registry = markersByIdRef.current;
    const nextIds = new Set();

    for (const property of results) {
      const id = propertyRowId(property);
      if (id === null || !propertyHasValidCoordinates(property)) continue;
      nextIds.add(id);
      if (!registry.has(id)) {
        addPropertyMarker(L, layer, property, registry);
      }
    }

    for (const [id, marker] of Array.from(registry.entries())) {
      if (nextIds.has(id)) continue;
      try {
        if (typeof layer.removeLayer === "function") {
          layer.removeLayer(marker);
        }
      } catch (_err) {
        /* ignore */
      }
      registry.delete(id);
    }

    if (loadState === "loaded" && shouldAutoFitRef.current) {
      shouldAutoFitRef.current = false;
      if (registry.size > 0) {
        fitMapToProperties(map, L, results);
      }
    }
  }, [results, loadState, mapMissing]);

  // ---------- Filter handlers ----------

  const handleApply = () => {
    writeFiltersToUrl(
      navigate,
      location.pathname,
      filterDraftRef.current,
      pairedSort,
    );
  };

  const handleReset = () => {
    syncFilterDraft(EMPTY_FILTER);
    writeFiltersToUrl(navigate, location.pathname, EMPTY_FILTER, pairedSort);
  };

  const handleLoadMore = () => {
    if (appendInFlightRef.current) return;
    if (appendLoading) return;
    if (loadState !== "loaded") return;
    if (!hasNextRef.current) return;

    const nextPage = loadedPageRef.current + 1;
    if (nextPage < 2) return;

    const baseParams = queryParamsRef.current;
    if (!baseParams) return;

    appendInFlightRef.current = true;
    setAppendLoading(true);
    setAppendError(null);

    const gen = queryGenRef.current;
    abortController(abortRef);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const params = { ...baseParams, page: nextPage };

    fetchProperties(params, { signal: ctrl.signal })
      .then((data) => {
        if (gen !== queryGenRef.current) return;
        const unwrapped = unwrapPaginatedList(data);
        const rows = accumulatePropertyRows(resultsRef.current, unwrapped.results);
        setResults(rows);
        setCount(unwrapped.count);
        setLoadedPage(nextPage);
        setHasNext(deriveHasNext(unwrapped, rows.length));
        setAppendError(null);
      })
      .catch((err) => {
        if (gen !== queryGenRef.current) return;
        if (ctrl.signal.aborted || isMapFetchAbortError(err)) return;
        setAppendError(messageFromError(err));
      })
      .finally(() => {
        if (abortRef.current === ctrl) {
          abortRef.current = null;
        }
        if (gen === queryGenRef.current) {
          appendInFlightRef.current = false;
          setAppendLoading(false);
        }
      });
  };

  // ---------- Floating control handlers ----------

  const handleFitAll = () => {
    const L = typeof window !== "undefined" ? window.L : null;
    const map = mapInstanceRef.current;
    fitMapToProperties(map, L, resultsRef.current);
  };

  const handleGeolocate = () => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      // Silent failure per directive R6.3 approval — no window.alert,
      // no toast. The button stays inert if the platform has no
      // geolocation API.
      return;
    }

    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const map = mapInstanceRef.current;
        if (map) {
          try {
            map.setView([pos.coords.latitude, pos.coords.longitude], 13);
          } catch (_err) {
            /* ignore — map torn down */
          }
        }
        setGeoLoading(false);
      },
      () => {
        // Silent failure — clear loading state, no blocking modal.
        setGeoLoading(false);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  };

  // ---------- Count node (progressive honesty) ----------
  //
  // Loaded-row count is unique accumulated Property rows, not marker
  // count. Partial catalogues surface "Завантажено X з N". A completed
  // catalogue falls back to the default toolbar count.

  const showProgressCount =
    loadState === "loaded"
    && count !== null
    && (hasNext || results.length < count);

  const countNode = showProgressCount ? (
    <>
      Завантажено <strong>{results.length}</strong> з{" "}
      <strong>{count}</strong> об'єктів
    </>
  ) : null;

  const showLoadMore = loadState === "loaded" && (hasNext || Boolean(appendError));

  // ---------- JSX ----------

  return (
    <section className="search-results map-results">
      <PropertyFilterPanel
        value={filterDraft}
        onChange={syncFilterDraft}
        onSubmit={handleApply}
        onReset={handleReset}
        offcanvasId="offcanvasFilter"
      />

      <PropertySearchToolbar
        count={loadState === "loaded" ? count : null}
        countNode={countNode}
        hideSort
        activeView="map"
        listHref={buildPairedSurfaceHref("/search", appliedFilters, {
          sort: pairedSort,
        })}
      />

      <div className="map-canvas-wrap">
        {mapMissing ? (
          <EmptyState
            icon="fa-solid fa-triangle-exclamation"
            title="Карта тимчасово недоступна"
            description="Не вдалося завантажити мапу. Оновіть сторінку або спробуйте пізніше."
            size="md"
          />
        ) : (
          <div
            ref={mapElRef}
            id="propertyMap"
            className="map-canvas"
            aria-label="Карта нерухомості"
          />
        )}

        <div className="map-floating">
          <button
            type="button"
            className="map-floating__btn"
            id="mapFitAll"
            aria-label="Показати всі об'єкти"
            onClick={handleFitAll}
          >
            <i className="fa-solid fa-expand" aria-hidden="true" />
          </button>
          <button
            type="button"
            className={`map-floating__btn${geoLoading ? " is-loading" : ""}`}
            id="mapGeolocate"
            aria-label="Моє місцезнаходження"
            onClick={handleGeolocate}
            disabled={geoLoading}
          >
            <i className="fa-solid fa-location-crosshairs" aria-hidden="true" />
          </button>
        </div>

        {showLoadMore ? (
          <div
            className="feature-realty-list__map-progress"
            aria-live="polite"
            data-loaded-page={loadedPage}
          >
            {hasNext ? (
              <button
                type="button"
                className="action-btn feature-realty-list__map-progress-btn"
                onClick={handleLoadMore}
                disabled={appendLoading}
                aria-busy={appendLoading ? "true" : "false"}
                data-testid="map-load-more"
              >
                {appendLoading
                  ? "Завантаження…"
                  : (appendError ? "Повторити" : `Показати ще ${MARKER_PAGE_SIZE}`)}
              </button>
            ) : null}
            {appendError ? (
              <div
                className="feature-realty-list__map-progress-error"
                role="alert"
                data-testid="map-append-error"
              >
                {appendError}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Error state — quiet inline notice. Map stays mounted so the
          user can still interact with whatever rows arrived previously
          (or the empty Ukraine-centered view on first failure).
          Append errors stay next to load-more and must not replace
          already loaded results. */}
      {loadState === "error" && loadError ? (
        <div className="feature-realty-list__engagement-error" role="alert">
          {loadError}
        </div>
      ) : null}
    </section>
  );
}

export default RealtyMapScreenContent;
